<template>
  <div class="w-full flex p-4 items-center justify-between flex-grow">
    <div>
      <h5>{{ giftCard.balanceLabel }}</h5>
      <div class="text-sm opacity-60 flex flex-wrap">
        <p class="mr-4 flex items-center">
          <span class="opacity-60 mr-1">
            {{ $t('general.colon', { label: $t('general.number') }) }}
          </span>
          <span>{{ giftCard.displayNumber }}</span>
        </p>
        <p class="mr-4 flex items-center">
          <span class="opacity-60 mr-1">
            {{ $t('general.colon', { label: $t('general.expires') }) }}
          </span>
          <span v-if="giftCard.expires">{{ $date.format( giftCard.expiryDate * 1000, $date.presets.long) }}</span>
          <span v-if="!giftCard.expires">{{ $t('giftCard.neverExpires') }}</span>
        </p>
      </div>
    </div>

    <div class="flex items-center">
      <div class="form-item--status mr-4">
        <o-chip v-if="isExpired" variant="danger" flat>
          {{ $t('general.expired') }}
        </o-chip>
        <o-chip v-if="isExpended" variant="warning" flat>
          {{ $t('giftCard.spent') }}
        </o-chip>
        <o-chip v-if="isPaid" variant="info" flat>
          {{ $t('general.paid') }}
        </o-chip>
        <o-chip v-if="isClaimable" variant="warning" flat>
          {{ $t('giftCard.unclaimed') }}
        </o-chip>
        <o-chip v-if="isGift && isClaimable" variant="warning" flat class="mb-1 mr-1">
          {{ $t('giftCard.unclaimed') }}
        </o-chip>
        <o-chip v-if="isGift && !isClaimable" variant="success" flat class="mb-1 mr-1">
          {{ $t('giftCard.claimed') }}
        </o-chip>
        <o-chip v-if="isGift && giftCard.gift.deliveryDate && isGiftUndelivered" variant="warning" flat>
          {{ $t('general.colonSeparator', { label1: $t('general.delivers'), label2: giftCard.gift.deliveryDateLabel }) }}
        </o-chip>
        <o-chip v-if="isGift && isGiftDelivered" variant="success" flat>
          {{ $t('general.delivered') }}
        </o-chip>
        <o-chip v-if="isGift && isGiftFailed" variant="success" flat>
          {{ $t('general.failed') }}
        </o-chip>
      </div>
      <div class="flex items-center space-x-1">
        <o-tooltip :text="$t('giftCard.edit')">
          <o-panel-link :path="`/customer/giftCard/${giftCard.id}`" tag="o-button" flat size="sm" icon="edit" />
        </o-tooltip>
        <o-tooltip v-if="!hasOrder" :text="$t('order.new')">
          <o-button flat size="sm" icon="sale" variant="primary" @click="handleCreateOrder" />
        </o-tooltip>
        <o-tooltip v-if="hasOrder" :text="$t('order.view')">
          <o-panel-link tag="o-button" flat size="sm" icon="sale" :path="`/order/${giftCard.order.idOrder}`" />
        </o-tooltip>
        <o-tooltip :text="$t('general.email')">
          <o-button flat size="sm" icon="email" @click="handleEmail" />
        </o-tooltip>
        <o-tooltip :text="$t('general.delete')">
          <o-button flat size="sm" icon="delete" variant="danger" @click="handleDelete" />
        </o-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  giftCard: Record<string, any>
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'delete', giftCard: any): void
  (e: 'email', giftCard: any): void
  (e: 'print', giftCard: any): void
  (e: 'createOrder', giftCard: any): void
}>()

const isExpended = computed(() => props.giftCard.isExpended)
const isExpired = computed(() => props.giftCard.isExpired)
const isValid = computed(() => props.giftCard.isValid)
const isPaid = computed(() => props.giftCard.isPaid)
const isClaimable = computed(() => props.giftCard.claimable)
const isGift = computed(() => props.giftCard.isGift)
const isGiftDelivered = computed(() => props.giftCard.isGiftDelivered)
const isGiftFailed = computed(() => props.giftCard.isGiftFailed)
const isGiftUndelivered = computed(() => props.giftCard.isGiftUndelivered)
const hasOrder = computed(() => !!props.giftCard.order)

const handleDelete = () => {
  emit('delete', props.giftCard)
}

const handleEmail = () => {
  emit('email', props.giftCard)
}

const handlePrint = () => {
  emit('print', props.giftCard)
}

const handleCreateOrder = () => {
  emit('createOrder', props.giftCard)
}
</script>
